import Pressable from '@/components/motion/pressable';
import { ROUTES } from '@/app/routes';
import RoleBadge from '@/components/admin/role-badge';
import type { IAdminUser } from '@/entities/admin-user.entity';
import { formatProfitUsd } from '@/lib/format-profit';
import { cn } from '@/lib/utils';
import { BanIcon, BoxIcon, CalendarIcon, UsersIcon, WalletIcon } from 'lucide-react';
import { memo } from 'react';
import { useNavigate } from 'react-router';
import TimeAgo from 'react-timeago';
import russianStrings from 'react-timeago/lib/language-strings/ru';
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter';

const formatter = buildFormatter(russianStrings);

const UserCard = memo(function UserCard({ user }: { user: IAdminUser }) {
	const navigate = useNavigate();
	const isBanned = user.isBanned || user.userIsBanned;

	return (
		<Pressable
			role='button'
			tabIndex={0}
			className={cn(
				'p-3 bg-card rounded-md flex flex-col gap-2 border border-border border-l-2 cursor-pointer active:bg-accent/30',
				isBanned && 'opacity-60',
			)}
			style={{ borderLeftColor: isBanned ? 'var(--destructive)' : 'var(--chart-2)' }}
			onClick={() => navigate(`${ROUTES.adminUser}${user.id}`)}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					navigate(`${ROUTES.adminUser}${user.id}`);
				}
			}}
		>
			<div className='flex items-center justify-between gap-2'>
				<span className='font-semibold truncate'>#{user.nickname}</span>
				<div className='flex items-center gap-1.5 shrink-0'>
					{isBanned && (
						<span className='inline-flex items-center gap-1 text-xs text-destructive'>
							<BanIcon size={12} />
							Забанен
						</span>
					)}
					<RoleBadge role={user.role} />
				</div>
			</div>

			<div className='grid grid-cols-2 gap-2 text-sm'>
				<div className='flex items-center gap-1.5 text-muted-foreground'>
					<WalletIcon size={14} className='opacity-50 shrink-0' />
					<span className='tabular-nums text-foreground font-medium'>
						${formatProfitUsd(user.profitAmount)} · {user.profitCount}
					</span>
				</div>
				<div className='flex items-center gap-1.5 text-muted-foreground'>
					<UsersIcon size={14} className='opacity-50 shrink-0' />
					<span>
						<span className='text-chart-2 font-medium'>{user.onlineClientsCount}</span>
						<span> / {user.clientsCount}</span>
					</span>
				</div>
				<div className='flex items-center gap-1.5 text-muted-foreground'>
					<BoxIcon size={14} className='opacity-50 shrink-0' />
					<span>{user.buildsCount} билдов</span>
				</div>
				<div className='flex items-center gap-1.5 text-muted-foreground'>
					<CalendarIcon size={14} className='opacity-50 shrink-0' />
					<TimeAgo date={user.createdAt} formatter={formatter} />
				</div>
			</div>
		</Pressable>
	);
});

export default UserCard;
